import { useState, useCallback, useRef, useEffect } from "react";
import {
    GoogleMap,
    useJsApiLoader,
    Marker,
    Autocomplete,
} from "@react-google-maps/api";
import { Input, Spinner } from "@heroui/react";
import { LuMapPin, LuSearch } from "react-icons/lu";
import { ThemedText } from "@/components/ui";
import { DEFAULT_MAP_CENTER, GOOGLE_MAPS_API_KEY } from "@/constants";

const libraries = ["places"];

const mapContainerStyle = {
    width: "100%",
    height: "300px",
    borderRadius: "12px",
};

const mapOptions = {
    disableDefaultUI: false,
    zoomControl: true,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: true,
};

/**
 * MapPicker Component
 * Interactive Google Maps picker with search autocomplete and click-to-place marker
 *
 * @param {Object} props
 * @param {Object} props.value - { lat, lng, formattedAddress }
 * @param {Function} props.onChange - Callback when location changes
 * @param {Object} props.defaultCenter - Default map center { lat, lng }
 * @param {boolean} props.showSelectedInfo - Show selected location info box (default: false)
 */
const MapPicker = ({
    value,
    onChange,
    defaultCenter: propDefaultCenter,
    showSelectedInfo = false,
}) => {
    const [markerPosition, setMarkerPosition] = useState(
        value?.lat && value?.lng ? { lat: value.lat, lng: value.lng } : null,
    );
    const [searchValue, setSearchValue] = useState("");
    const autocompleteRef = useRef(null);
    const mapRef = useRef(null);
    const geocoderRef = useRef(null);

    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries,
    });

    // Initialize geocoder when loaded
    useEffect(() => {
        if (isLoaded && window.google) {
            geocoderRef.current = new window.google.maps.Geocoder();
        }
    }, [isLoaded]);

    // Sync external value changes
    useEffect(() => {
        if (value?.lat && value?.lng) {
            // Parse as numbers in case they come as strings from API
            const lat = parseFloat(value.lat);
            const lng = parseFloat(value.lng);
            if (!isNaN(lat) && !isNaN(lng)) {
                setMarkerPosition({ lat, lng });
            }
        }
    }, [value?.lat, value?.lng]);

    // Reverse geocode to get address from coordinates
    const reverseGeocode = useCallback(
        (lat, lng) => {
            if (!geocoderRef.current) return;

            geocoderRef.current.geocode(
                { location: { lat, lng } },
                (results, status) => {
                    if (status === "OK" && results[0]) {
                        const place = results[0];
                        const addressData = parseAddressComponents(
                            place.address_components,
                        );

                        onChange?.({
                            lat,
                            lng,
                            formattedAddress: place.formatted_address,
                            googleMapUrl: `https://www.google.com/maps?q=${lat},${lng}`,
                            ...addressData,
                        });
                    }
                },
            );
        },
        [onChange],
    );

    // Parse Google address components for Indonesian addresses
    const parseAddressComponents = (components) => {
        const result = {
            village: "", // Kelurahan
            district: "", // Kecamatan
            city: "", // Kota/Kabupaten
            province: "", // Provinsi
            postalCode: "", // Kode Pos
        };

        components?.forEach((component) => {
            const types = component.types;

            // Kelurahan (Village) - multiple possible types in Indonesia
            if (
                types.includes("administrative_area_level_4") ||
                types.includes("sublocality_level_1") ||
                types.includes("sublocality")
            ) {
                if (!result.village) {
                    result.village = component.long_name;
                }
            }

            // Kecamatan (District)
            if (
                types.includes("administrative_area_level_3") ||
                types.includes("sublocality_level_2")
            ) {
                if (!result.district) {
                    result.district = component.long_name;
                }
            }

            // Kota/Kabupaten (City)
            if (
                types.includes("administrative_area_level_2") ||
                types.includes("locality")
            ) {
                if (!result.city) {
                    result.city = component.long_name;
                }
            }

            // Provinsi (Province)
            if (types.includes("administrative_area_level_1")) {
                result.province = component.long_name;
            }

            // Kode Pos (Postal Code)
            if (types.includes("postal_code")) {
                result.postalCode = component.long_name;
            }
        });

        return result;
    };

    // Handle map click
    const handleMapClick = useCallback(
        (event) => {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();

            setMarkerPosition({ lat, lng });
            reverseGeocode(lat, lng);
        },
        [reverseGeocode],
    );

    // Handle place selection from autocomplete
    const handlePlaceSelect = useCallback(() => {
        const place = autocompleteRef.current?.getPlace();

        if (place?.geometry?.location) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();

            setMarkerPosition({ lat, lng });
            setSearchValue(place.formatted_address || "");

            // Pan map to location
            if (mapRef.current) {
                mapRef.current.panTo({ lat, lng });
                mapRef.current.setZoom(17);
            }

            const addressData = parseAddressComponents(
                place.address_components,
            );

            onChange?.({
                lat,
                lng,
                formattedAddress: place.formatted_address,
                googleMapUrl: `https://www.google.com/maps?q=${lat},${lng}`,
                ...addressData,
            });
        }
    }, [onChange]);

    const onMapLoad = useCallback((map) => {
        mapRef.current = map;
    }, []);

    const onAutocompleteLoad = useCallback((autocomplete) => {
        autocompleteRef.current = autocomplete;
    }, []);

    if (loadError) {
        return (
            <div className="bg-danger-50 rounded-xl p-4 text-center">
                <ThemedText variant="danger">
                    Gagal memuat Google Maps. Periksa API key Anda.
                </ThemedText>
            </div>
        );
    }

    if (!isLoaded) {
        return (
            <div className="bg-default-100 flex h-[300px] items-center justify-center rounded-xl">
                <Spinner label="Memuat peta..." />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3">
            {/* Search Input */}
            <Autocomplete
                onLoad={onAutocompleteLoad}
                onPlaceChanged={handlePlaceSelect}
                options={{
                    componentRestrictions: { country: "id" },
                }}
            >
                <Input
                    placeholder="Cari alamat atau klik pada peta..."
                    value={searchValue}
                    onValueChange={setSearchValue}
                    variant="bordered"
                    startContent={
                        <LuSearch size={16} className="text-default-400" />
                    }
                    radius="sm"
                />
            </Autocomplete>

            {/* Map */}
            <div className="border-default-200 relative overflow-hidden rounded-xl border">
                <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={
                        markerPosition ||
                        propDefaultCenter ||
                        DEFAULT_MAP_CENTER
                    }
                    zoom={markerPosition ? 17 : 12}
                    onClick={handleMapClick}
                    onLoad={onMapLoad}
                    options={mapOptions}
                >
                    {markerPosition && (
                        <Marker
                            position={markerPosition}
                            animation={window.google.maps.Animation.DROP}
                        />
                    )}
                </GoogleMap>
            </div>

            {/* Selected Location Info */}
            {showSelectedInfo && value?.formattedAddress && (
                <div className="bg-success-50 flex items-start gap-2 rounded-lg p-3">
                    <LuMapPin size={16} className="text-success mt-0.5" />
                    <div className="flex flex-col">
                        <ThemedText size="xs" variant="secondary">
                            Lokasi dipilih:
                        </ThemedText>
                        <ThemedText size="sm">
                            {value.formattedAddress}
                        </ThemedText>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MapPicker;
